import { bookingApiSlice } from '../api/booking/bookingApiSlice';
import { seriesBookingApiSlice } from '../api/booking/seriesBookingApiSlice';
import Logger from '../common/logger';
import { store } from '../store/store';
import { CreateSeriesPayload, CreateSeriesResponse, SeriesContinuation, SeriesRequestPayload } from '../types/bookingCreation';
import { getApiErrorMessage } from '../utils/apiError';
import { PreviewOccurrence, ServerSeriesPattern } from '../utils/recurrence';

/**
 * The series module's two calls (series plan §4.2). Both refuse a request the
 * server cannot book as asked with a message, which is handed back so the
 * screen can show the reason — a failure must not look like "no dates".
 */
export const previewSeries = async (
    data: SeriesRequestPayload,
): Promise<{ occurrences: PreviewOccurrence[] } | { error: string }> => {
    try {
        return await store.dispatch(seriesBookingApiSlice.endpoints.previewSeries.initiate(data)).unwrap();
    } catch (error) {
        Logger('previewSeries').error(error);
        return { error: getApiErrorMessage(error) ?? 'error' };
    }
};

export const createSeries = async (
    data: CreateSeriesPayload,
    saveMode: 'save_only' | 'save_and_send_email',
): Promise<CreateSeriesResponse | { error: string }> => {
    try {
        const response = await store.dispatch(
            seriesBookingApiSlice.endpoints.createSeries.initiate({ data, saveMode }),
        ).unwrap();
        // The booking list and details are served by `bookingApiSlice` (its own
        // tag namespace), as in editSeriesBookingFlow.
        store.dispatch(bookingApiSlice.util.invalidateTags(['bookingList', 'bookingDetails']));
        return response;
    } catch (error) {
        Logger('createSeries').error(error);
        return { error: getApiErrorMessage(error) ?? 'error' };
    }
};

/** The next dates of a series' stored rule, or the server's reason it has none. */
export const continueSeries = async (
    seriesId: string,
    endCondition: ServerSeriesPattern['endCondition'],
): Promise<SeriesContinuation | { error: string }> => {
    try {
        return await store.dispatch(
            seriesBookingApiSlice.endpoints.continueSeries.initiate({ seriesId, endCondition }),
        ).unwrap();
    } catch (error) {
        Logger('continueSeries').error(error);
        return { error: getApiErrorMessage(error) ?? 'error' };
    }
};
